import { cloneDeep } from 'lodash';
import type { EstimateVisibility } from '../interfaces/estimate/estimate.interface';
import { Address } from '../interfaces/address.interface';
import { resolveEstimateVisibility, StoredEstimateVisibility } from './estimate-visibility';

/**
 * Mirrors ProjulV1Portal's src/app/shared/Utils/estimate-document-header.ts, minus its capture,
 * reset and print-projection helpers: this app only ever reads a header, it never writes one.
 */

/** Addresses lose their row `Id` on the way into a stored override, so it is never relied on here. */
export type EstimateHeaderAddress = Address & { Id?: string };

/**
 * A company phone reaches the header either as text or as the object the portal's phone input binds,
 * which is why the estimate template reads `CompanyPhoneNo?.number` before falling back to the text.
 */
export interface EstimateHeaderPhoneNumber {
  number?: string;
}
export type EstimateHeaderCompanyPhone = string | EstimateHeaderPhoneNumber;

/**
 * The facts a shared estimate draws in its header. `CompanyLogo` is carried for wire parity with
 * the portal; this app resolves the logo image out of `Files` instead.
 */
export interface EstimateDocumentHeaderFacts {
  CompanyLogo?: string;
  /** A key from `estimateLogoSizes`; absent means the document never chose one. */
  LogoSize?: number;
  CompanyName: string;
  LicenseNumberName: string;
  LicenseNumber: string;
  CompanyAddress: EstimateHeaderAddress;
  CompanyPhoneNo: EstimateHeaderCompanyPhone;
  CompanyEmail: string;
  CompanyWebsite: string;

  CustomerFirstName: string;
  CustomerLastName: string;
  CustomerCompanyName: string;
  DisplayAsCustomerCompanyName: boolean;
  /** Always flattened to text upstream, out of the customer's first phone number. */
  CustomerPhone: string;
  CustomerEmail: string;
  CustomerAddress: EstimateHeaderAddress;
  CustomerBillingAddress: EstimateHeaderAddress;
  ShowBillingAddressToCustomer: boolean;

  ProjectName: string;
  ProjectNumber: string;
  ProjectAddress: EstimateHeaderAddress;
  ShowProjectInfoToCustomer: boolean;
}

/** What one document stores when it stops inheriting: the whole header, values and flags together. */
export interface EstimateDocumentHeaderOverride extends EstimateDocumentHeaderFacts {
  Visibility: StoredEstimateVisibility;
}

/**
 * A resolved header: facts are optional here and only here, because one the source omitted resolves
 * to `undefined` rather than inheriting, and identity always comes from the estimate, never a source.
 */
export interface EstimateDocumentHeader extends Partial<EstimateDocumentHeaderFacts> {
  Visibility: EstimateVisibility;
  CustomerId?: string;
  ProjectId?: string;
}

/** Anything that can supply header facts: the estimate itself, or a stored override. */
export type EstimateHeaderSource = Partial<EstimateDocumentHeaderFacts> & {
  CustomerId?: string;
  ProjectId?: string;
  Visibility?: StoredEstimateVisibility | null;
};

/**
 * What a detail exposes to carry its own header. `EstimateDetailsEntityDTO` declares both fields;
 * its whitelist constructor must copy them, which the change-order header spec proves.
 */
export interface EstimateHeaderOverrideCarrier {
  HeaderOverride?: string | null;
  HeaderOverrideData?: EstimateDocumentHeaderOverride | null;
}

/**
 * The independent field oracle for the header, in the order the portal writes them. Every key is
 * materialised on resolve, so an override that omitted one prints empty rather than inheriting.
 */
export const HEADER_FACT_KEYS: readonly (keyof EstimateDocumentHeaderFacts)[] = [
  'CompanyLogo',
  'LogoSize',
  'CompanyName',
  'LicenseNumberName',
  'LicenseNumber',
  'CompanyAddress',
  'CompanyPhoneNo',
  'CompanyEmail',
  'CompanyWebsite',

  'CustomerFirstName',
  'CustomerLastName',
  'CustomerCompanyName',
  'DisplayAsCustomerCompanyName',
  'CustomerPhone',
  'CustomerEmail',
  'CustomerAddress',
  'CustomerBillingAddress',
  'ShowBillingAddressToCustomer',

  'ProjectName',
  'ProjectNumber',
  'ProjectAddress',
  'ShowProjectInfoToCustomer',
];

/**
 * How a caller is told a stored override could not be read, so the degrade to the parent header is
 * reported rather than swallowed. The raw blob is handed over because only it explains the failure.
 */
export type UnreadableOverrideReporter = (serialised: string, error: unknown) => void;

function parseStoredOverride(
  serialised: string | null | undefined,
  onUnreadable?: UnreadableOverrideReporter
): EstimateDocumentHeaderOverride | null {
  if (!serialised) return null;
  try {
    const parsed = JSON.parse(serialised);
    return parsed && typeof parsed === 'object' ? (parsed as EstimateDocumentHeaderOverride) : null;
  } catch (error) {
    // Inheriting the parent header keeps the document readable, but it also re-applies the parent's
    // visibility, which can show a field this document chose to hide. The caller has to hear about it.
    onUnreadable?.(serialised, error);
    return null;
  }
}

/** The parsed companion wins when one is present; otherwise the serialised string. */
export function readHeaderOverride(
  detail: unknown,
  onUnreadable?: UnreadableOverrideReporter
): EstimateDocumentHeaderOverride | null {
  const carrier = detail as EstimateHeaderOverrideCarrier | null | undefined;
  return carrier?.HeaderOverrideData || parseStoredOverride(carrier?.HeaderOverride, onUnreadable);
}

/** One fact at a time, so the key relates target to source instead of widening both to a bag. */
function copyFact<K extends keyof EstimateDocumentHeaderFacts>(
  target: Partial<EstimateDocumentHeaderFacts>,
  source: Partial<EstimateDocumentHeaderFacts>,
  key: K
): void {
  target[key] = cloneDeep(source[key]);
}

/**
 * THE single reader of a shared document's header. Returns a detached copy, so nothing downstream
 * can write back through it onto the payload.
 */
export function resolveDocumentHeader(
  estimate: EstimateHeaderSource | null | undefined,
  detail: unknown,
  onUnreadableOverride?: UnreadableOverrideReporter
): EstimateDocumentHeader {
  const override = readHeaderOverride(detail, onUnreadableOverride);
  const source: EstimateHeaderSource = override || estimate || {};
  const facts: Partial<EstimateDocumentHeaderFacts> = {};
  HEADER_FACT_KEYS.forEach((key) => copyFact(facts, source, key));
  return {
    ...facts,
    Visibility: resolveEstimateVisibility(source),
    CustomerId: estimate?.CustomerId,
    ProjectId: estimate?.ProjectId,
  };
}
